import { Box, TextField, Typography, useTheme } from "@mui/material";
import Grid from '@mui/material/Grid';
import { PhotoProduct, QuantityPrice } from 'common-models';

import { ChangeEvent, FC, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import QuantityPriceEditor from './QuantityPriceEditor';


type PhotoProductEditorProps = {
    photoProduct: PhotoProduct,
};
const addPrice = (prices: QuantityPrice[]) => {
    const temp = new Array<QuantityPrice>(...prices);
    temp.push({
        priceInSekExclVat: 0,
        quantity: 0,
        id: uuidv4()
    })
    return temp;


};
const PhotoProductPriceEditor: FC<PhotoProductEditorProps> = ({ photoProduct }) => {

    const [prices, setPrices] = useState(photoProduct.prices);



    useEffect(() => {
        const lastPrice: QuantityPrice = prices.length > 0
            ? prices[prices.length - 1]
            : { id: '', priceInSekExclVat: 1, quantity: 1 }

        if (lastPrice.priceInSekExclVat !== 0 && lastPrice.quantity !== 0) {
            const updatedPrices = addPrice(prices);
            setPrices(updatedPrices)
        }
    }, [prices]);

    useEffect(() => {
        photoProduct.prices = prices.filter(x => x.priceInSekExclVat !== 0 && x.quantity !== 0);
    }, [photoProduct, prices]);


    const onChangeNamesSv = async (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        photoProduct.names = { ...photoProduct.names, sv: event.target.value }
    }

    const onChangeNamesEn = async (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        photoProduct.names = { ...photoProduct.names, en: event.target.value }
    }

    const onChangeQuantity = (changedPrice: QuantityPrice, newQuantity: number) => {
        const price = prices.find(x => x.id === changedPrice.id);
        if (price !== undefined) {
            price.quantity = newQuantity;
        }
        setPrices(new Array<QuantityPrice>(...prices));
    }

    const onChangeQuantityPrice = (changedPrice: QuantityPrice, newPrice: number) => {
        const price = prices.find(x => x.id === changedPrice.id);
        if (price !== undefined) {
            price.priceInSekExclVat = newPrice;
        }
        setPrices(new Array<QuantityPrice>(...prices));
    }

    const theme = useTheme();

    return (
        <Box sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
            p: 2,
            backgroundColor: theme.palette.background.paper
        }}>
            {/* Product Names Section */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: theme.typography.fontWeightMedium }}>
                    Product Names
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            defaultValue={photoProduct.names.sv}
                            variant="outlined"
                            label="Name (Swedish)"
                            onChange={onChangeNamesSv}
                            size="small"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            defaultValue={photoProduct.names.en}
                            variant="outlined"
                            label="Name (English)"
                            onChange={onChangeNamesEn}
                            size="small"
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Pricing Section */}
            <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: theme.typography.fontWeightMedium }}>
                    Quantity Pricing
                </Typography>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    '& > *:not(:last-child)': {
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        pb: 1,
                        mb: 1
                    }
                }}>
                    {prices.map((price, index) => {
                        const isPlaceholder = price.priceInSekExclVat === 0 && price.quantity === 0;
                        const isLastItem = index === prices.length - 1;

                        return (
                            <Box
                                key={price.id || `price-${index}`}
                                sx={{
                                    ...(isPlaceholder && isLastItem && {
                                        border: `2px dashed ${theme.palette.primary.main}`,
                                        borderRadius: theme.shape.borderRadius,
                                        backgroundColor: theme.palette.action.hover,
                                        position: 'relative',
                                        '&::before': {
                                            content: '"Add new price tier"',
                                            position: 'absolute',
                                            top: -10,
                                            left: 8,
                                            backgroundColor: theme.palette.background.paper,
                                            color: theme.palette.primary.main,
                                            fontSize: '0.75rem',
                                            fontWeight: theme.typography.fontWeightMedium,
                                            px: 1,
                                            zIndex: 1
                                        }
                                    })
                                }}
                            >
                                <QuantityPriceEditor
                                    originalQuantityPrice={price}
                                    onQuantityChangedCallback={onChangeQuantity}
                                    onPriceChangedCallback={onChangeQuantityPrice}
                                    isPlaceholder={isPlaceholder && isLastItem}
                                />
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    )
}


// const PREFIX = 'PhotoProductPriceEditor';
// const classes = {
//     pricesContainer: `${PREFIX}-pricesContainer`,
//     pricesList: `${PREFIX}-pricesList`,
//     quantityTextFieldContainer: `${PREFIX}-quantityTextFieldContainer`,
//     quantityTextField: `${PREFIX}-quantityTextField`,
//     priceTextField: `${PREFIX}-priceTextField`,
// }

// const Root = styled('div')(({ theme }) => ({
//     [`&.${classes.pricesContainer}`]: {
//         marginBottom: '1rem',
//         borderLeftStyle: 'solid',
//         borderBottomStyle: 'solid',
//         borderWidth: '1px',
//     },
//     [`&.${classes.pricesList}`]: {
//         marginLeft: '1rem'
//     },
//     [`&.${classes.quantityTextFieldContainer}`]: {
//         padding: '1rem',
//     },
//     [`&.${classes.quantityTextField}`]: {
//         width: '5rem',
//         margin: '1rem',
//     },
//     [`&.${classes.priceTextField}`]: {
//         width: '10rem',
//         margin: '1rem',
//         padding: '1rem'
//     },
// }));

export default PhotoProductPriceEditor;
